'use client';

import { c as _c } from "react/compiler-runtime";
import { FOCUS_RING } from '@common/components/interaction';
import { cn } from '@core/utils';
import { useState } from 'react';
import { axisIndexesOf, axisLabelOf, axisMaxOf, formatAxisCount } from '../../_utils/report/report';
import type { PartnersReportBucket, ReportUnit } from '../../_utils/types';
type Device = 'mobile' | 'desktop';
export const UNIT_TITLE: Record<ReportUnit, string> = {
  DAY: '일별',
  WEEK: '주별',
  MONTH: '월별'
};
const AXIS_COUNT: Record<Device, number> = {
  mobile: 3,
  desktop: 6
};
const COLUMN_WIDTH = 48;
const TOOLTIP_HALF_WIDTH = 56;
export const REPORT_SERIES = [{
  key: 'clicks',
  label: '클릭수',
  stroke: 'stroke-B-500',
  dot: 'border-B-500',
  swatch: 'bg-B-500'
}, {
  key: 'donations',
  label: '기부수',
  stroke: 'stroke-Gray-900',
  dot: 'border-Gray-900',
  swatch: 'bg-Gray-900'
}] as const;

//* 두 선이 겹치면(대개 둘 다 0) 나중에 그린 선만 보이므로 주 지표인 클릭수를 마지막에 그린다
const DRAW_ORDER = [...REPORT_SERIES].reverse();
interface ReportLineGraphProps {
  device: Device;
  series: PartnersReportBucket[];
  unit: ReportUnit;
  className: string;
}
export const ReportLineGraph = t0 => {
  const $ = _c(52);
  const {
    device,
    series,
    unit,
    className
  } = t0;
  const [activeIndex, setActiveIndex] = useState(null);
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== activeIndex || $[1] !== className || $[2] !== device || $[3] !== series || $[4] !== unit) {
    let t13;
    if ($[17] !== unit) {
      t13 = bucket => ({
        key: bucket.bucketStartDate,
        axisLabel: axisLabelOf(bucket.bucketStartDate, unit)
      });
      $[17] = unit;
      $[18] = t13;
    } else {
      t13 = $[18];
    }
    const buckets = series.map(t13);
    const counts = series.map(_temp);
    const axisMax = axisMaxOf(Math.max(0, ...counts.map(_temp2)));
    const xOf = index => (index + 0.5) / buckets.length * 100;
    let t14;
    if ($[19] !== axisMax) {
      t14 = count => 100 - count / axisMax * 100;
      $[19] = axisMax;
      $[20] = t14;
    } else {
      t14 = $[20];
    }
    const yOf = t14;
    const isScrollable = device === "mobile" && buckets.length > 7;
    const labelIndexes = isScrollable || buckets.length <= 12 ? buckets.map(_temp3) : axisIndexesOf(buckets.length, AXIS_COUNT[device]);
    const activeCount = activeIndex === null ? null : counts[activeIndex];
    if ($[21] !== className) {
      t12 = cn("flex-col gap-12", className);
      $[21] = className;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    t10 = "flex";
    const t15 = isScrollable ? "mt-48" : "mt-16";
    let t16;
    if ($[23] !== t15) {
      t16 = cn("relative h-100 w-36 shrink-0 text-Gray-500 typo-caption1", t15);
      $[23] = t15;
      $[24] = t16;
    } else {
      t16 = $[24];
    }
    t11 = <div aria-hidden={true} className={t16}>{[axisMax, axisMax / 2, 0].map((tick, index_1) => <span key={tick} className="absolute left-0 -translate-y-1/2" style={{
        top: `${index_1 * 50}%`
      }}>{series.length > 0 ? formatAxisCount(tick) : "-"}</span>)}</div>;
    t7 = `${buckets[0]?.key}-${buckets.length}`;
    if ($[25] !== isScrollable) {
      t8 = node => {
        if (node && isScrollable) {
          node.scrollLeft = node.scrollWidth;
        }
      };
      $[25] = isScrollable;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    const t17 = isScrollable && "overflow-x-auto hide-scrollbar";
    if ($[27] !== t17) {
      t9 = cn("min-w-0 flex-1", t17);
      $[27] = t17;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    t4 = isScrollable ? "pt-48" : "pt-16";
    t5 = isScrollable ? {
      width: `${buckets.length * COLUMN_WIDTH}px`
    } : undefined;
    let t18;
    let t19;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = () => setActiveIndex(null);
      t19 = [0, 50, 100].map(_temp4);
      $[29] = t18;
      $[30] = t19;
    } else {
      t18 = $[29];
      t19 = $[30];
    }
    t6 = <div role="group" aria-label={`${UNIT_TITLE[unit]} 클릭수와 기부수 선 그래프`} className="relative h-100" onMouseLeave={t18}>{t19}<div className="absolute inset-0 flex">{buckets.map((bucket_1, index_2) => <button type="button" key={bucket_1.key} aria-label={`${bucket_1.axisLabel} 클릭 ${counts[index_2].clicks} · 기부 ${counts[index_2].donations}`} className={cn(FOCUS_RING, "relative min-w-0 flex-1 cursor-pointer p-0")} onMouseEnter={() => setActiveIndex(index_2)} onFocus={() => setActiveIndex(index_2)} onBlur={() => setActiveIndex(null)} onClick={() => setActiveIndex(index_2)} />)}</div><svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden={true} className="pointer-events-none absolute inset-0 size-full overflow-visible">{DRAW_ORDER.map(line => <polyline key={line.key} points={counts.map((item_0, index_3) => `${xOf(index_3)},${yOf(item_0[line.key])}`).join(" ")} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" className={line.stroke} />)}</svg>{counts.map((item_1, index_4) => DRAW_ORDER.map(line_0 => <span key={`${buckets[index_4].key}-${line_0.key}`} aria-hidden={true} className={cn("pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-pill border-2 bg-Gray-white", activeIndex === index_4 ? "size-12" : "size-8", line_0.dot)} style={{
        left: `${xOf(index_4)}%`,
        top: `${yOf(item_1[line_0.key])}%`
      }} />))}{activeIndex !== null && activeCount ? <span className="pointer-events-none absolute -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-xsmall bg-Gray-900 px-12 py-8 text-center text-Gray-white typo-caption1" style={{
        left: `clamp(${TOOLTIP_HALF_WIDTH}px, ${xOf(activeIndex)}%, calc(100% - ${TOOLTIP_HALF_WIDTH}px))`,
        top: `calc(${yOf(Math.max(activeCount.clicks, activeCount.donations))}% - 12px)`
      }}><span className="text-Gray-300">{buckets[activeIndex].axisLabel}</span><br />클릭 {activeCount.clicks.toLocaleString("ko-KR")} · 기부{" "}{activeCount.donations.toLocaleString("ko-KR")}</span> : null}</div>;
    t1 = true;
    t2 = "relative mt-8 h-20 text-Gray-500 typo-caption1";
    t3 = labelIndexes.map(index_5 => <span key={buckets[index_5].key} className="absolute -translate-x-1/2 whitespace-nowrap" style={{
      left: `${xOf(index_5)}%`
    }}>{buckets[index_5].axisLabel}</span>);
    $[0] = activeIndex;
    $[1] = className;
    $[2] = device;
    $[3] = series;
    $[4] = unit;
    $[5] = t1;
    $[6] = t10;
    $[7] = t11;
    $[8] = t12;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t1 = $[5];
    t10 = $[6];
    t11 = $[7];
    t12 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t13;
  if ($[31] !== t1 || $[32] !== t2 || $[33] !== t3) {
    t13 = <div aria-hidden={t1} className={t2}>{t3}</div>;
    $[31] = t1;
    $[32] = t2;
    $[33] = t3;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6) {
    t14 = <div className={t4} style={t5}>{t6}{t13}</div>;
    $[35] = t13;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t14 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t15 = <div key={t7} ref={t8} className={t9}>{t14}</div>;
    $[40] = t14;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t15) {
    t16 = <div className={t10}>{t11}{t15}</div>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t12 || $[50] !== t16) {
    t17 = <div className={t12}>{t16}</div>;
    $[49] = t12;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
};
function _temp(bucket_0) {
  return {
    clicks: bucket_0.clickCount,
    donations: bucket_0.donationCount
  };
}
function _temp2(item) {
  return Math.max(item.clicks, item.donations);
}
function _temp3(_, index_0) {
  return index_0;
}
function _temp4(top) {
  return <span key={top} aria-hidden={true} className="absolute inset-x-0 border-t border-Gray-100" style={{
    top: `${top}%`
  }} />;
}